import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderTypeTag } from "../components/OrderTypeTag";
import api, { formatError } from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import ClientImportantRemark from "../components/ClientImportantRemark";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor, moduleCanDo } from "../lib/modules";
import { toast } from "sonner";
import { Plus, Eye, Search, Truck, Receipt, FileText, Trash2, X, ShoppingBag, Users } from "lucide-react";
import ClientFormDialog from "../components/ClientFormDialog";
import { INDIAN_STATES } from "../lib/indianStates";
import InvoiceWorkflow from "./InvoiceWorkflow";
import ServiceOrderManagement from "./ServiceOrderManagement";

const ORDER_STATUS_COLOR = {
  "Pending Invoice": "bg-amber-100 text-amber-800",
  "Pending Dispatch": "bg-blue-100 text-blue-700",
  "Partial Delivered": "bg-purple-100 text-purple-700",
  "Complete Delivered": "bg-green-100 text-green-700",
  "Cancelled": "bg-red-100 text-red-700",
};

const PAY_COLOR = {
  "Not Invoiced": "bg-slate-100 text-slate-600",
  "Pending Payment": "bg-amber-100 text-amber-800",
  "Paid": "bg-green-100 text-green-700",
  "Overdue": "bg-red-100 text-red-700",
  "Cancelled": "bg-slate-200 text-slate-600 line-through",
};

// ─────────────────────────────────────────────────────────────
// Filter chips — replaces verbose dropdowns for the Orders tabs.
// Each chip toggles a single filter key. Clicking an active chip
// clears it. A "Clear all" chip appears when any chip is active.
// ─────────────────────────────────────────────────────────────
function Chip({ label, active, onClick, tone = "indigo", testid }) {
  const activeCls = {
    indigo: "bg-indigo-600 text-white border-indigo-600 shadow-sm",
    amber:  "bg-amber-500 text-white border-amber-500 shadow-sm",
    emerald:"bg-emerald-600 text-white border-emerald-600 shadow-sm",
    rose:   "bg-rose-600 text-white border-rose-600 shadow-sm",
    slate:  "bg-slate-700 text-white border-slate-700 shadow-sm",
    sky:    "bg-sky-600 text-white border-sky-600 shadow-sm",
    violet: "bg-violet-600 text-white border-violet-600 shadow-sm",
  }[tone] || "bg-indigo-600 text-white border-indigo-600 shadow-sm";
  const idleCls = "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-slate-400";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold transition-all ${active ? activeCls : idleCls}`}
      data-testid={testid}
      data-active={active ? "true" : "false"}
    >
      {label}
    </button>
  );
}

function OrderFilterChips({ filters, setFilters, activeCount }) {
  const toggle = (key, val) => setFilters({ ...filters, [key]: filters[key] === val ? "" : val });
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="order-filter-chips">
      <span className="text-[10px] uppercase font-bold text-slate-500 mr-1">Status</span>
      <Chip label="Pending Invoice" active={filters.status === "Pending Invoice"} onClick={() => toggle("status", "Pending Invoice")} tone="amber" testid="chip-status-pending-invoice" />
      <Chip label="Pending Dispatch" active={filters.status === "Pending Dispatch"} onClick={() => toggle("status", "Pending Dispatch")} tone="sky" testid="chip-status-pending-dispatch" />
      <Chip label="Delivered" active={filters.status === "Complete Delivered"} onClick={() => toggle("status", "Complete Delivered")} tone="emerald" testid="chip-status-delivered" />
      <Chip label="Cancelled" active={filters.status === "Cancelled"} onClick={() => toggle("status", "Cancelled")} tone="rose" testid="chip-status-cancelled" />

      <span className="text-[10px] uppercase font-bold text-slate-500 ml-2 mr-1">Payment</span>
      <Chip label="Pending Payment" active={filters.payment === "Pending Payment"} onClick={() => toggle("payment", "Pending Payment")} tone="amber" testid="chip-pay-pending" />
      <Chip label="Paid" active={filters.payment === "Paid"} onClick={() => toggle("payment", "Paid")} tone="emerald" testid="chip-pay-paid" />
      <Chip label="Overdue" active={filters.payment === "Overdue"} onClick={() => toggle("payment", "Overdue")} tone="rose" testid="chip-pay-overdue" />

      <span className="text-[10px] uppercase font-bold text-slate-500 ml-2 mr-1">Type</span>
      <Chip label="Product" active={filters.order_type === "Product"} onClick={() => toggle("order_type", "Product")} tone="indigo" testid="chip-type-product" />
      <Chip label="Service" active={filters.order_type === "Service"} onClick={() => toggle("order_type", "Service")} tone="violet" testid="chip-type-service" />

      <span className="text-[10px] uppercase font-bold text-slate-500 ml-2 mr-1">GeM</span>
      <Chip label="GeM" active={filters.gem === "yes"} onClick={() => toggle("gem", "yes")} tone="indigo" testid="chip-gem-yes" />
      <Chip label="Non-GeM" active={filters.gem === "no"} onClick={() => toggle("gem", "no")} tone="slate" testid="chip-gem-no" />

      {activeCount > 0 && (
        <button
          type="button"
          onClick={() => setFilters({ status: "", payment: "", order_type: "", gem: "", sales_associate: "", date_from: "", date_to: "" })}
          className="ml-auto inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1 text-[11px] font-bold hover:bg-rose-100"
          data-testid="order-filter-clear"
        >
          <X size={11} /> Clear ({activeCount})
        </button>
      )}
    </div>
  );
}

export default function OrderWorkflow({ initialTab = "mine" } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  // Within Order/Invoice flows the "admin tier" includes Accounts Admin —
  // they share full power with Master Admin per business rule (Feb 2026).
  const isMaster = user?.role === "master_admin" || moduleCanDo(user, "sales_orders", "edit");
  const isAccounts = moduleCanDo(user, "sales_orders", "edit");
  const canEdit = isMaster || isAccounts;
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editRemark, setEditRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const q = location.state?.fromQuote;
    if (!q) return;
    setForm(formFromQuote(q));
    setOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    // Only fetch orders when viewing the Orders tabs.
    if (!["mine", "crafted", "all"].includes(tab)) return;
    try {
      const { data } = await api.get(`/orders?scope=${tab}`);
      setOrders(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const [filters, setFilters] = useState({ status: "", payment: "", order_type: "", gem: "", sales_associate: "", date_from: "", date_to: "" });
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return orders.filter((o) => {
      if (q && ![o.order_no, o.client_name, o.created_by_name, o.sale_associate_name]
        .some((v) => String(v ?? "").toLowerCase().includes(q))) return false;
      if (filters.status && o.status !== filters.status) return false;
      if (filters.payment && o.payment_status !== filters.payment) return false;
      if (filters.order_type && (o.order_type || "Product") !== filters.order_type) return false;
      if (filters.gem === "yes" && !o.is_gem_contract) return false;
      if (filters.gem === "no" && o.is_gem_contract) return false;
      if (filters.sales_associate && !String(o.sale_associate_name || "").toLowerCase().includes(filters.sales_associate.toLowerCase())) return false;
      if (filters.date_from && (o.created_at || "").slice(0, 10) < filters.date_from) return false;
      if (filters.date_to && (o.created_at || "").slice(0, 10) > filters.date_to) return false;
      return true;
    });
  }, [orders, search, filters]);
  const activeFilterCount = Object.values(filters).filter((v) => v).length;

  const submit = async () => {
    if (!form.client_name.trim() || !form.billing_address.trim()) {
      toast.error("Client and billing address are required");
      return;
    }
    const totalNum = parseFloat(String(form.total_value || "").replace(/,/g, ""));
    if (!totalNum || totalNum <= 0) {
      toast.error("Total Order Value (INR) is mandatory");
      return;
    }
    if (form.commission_value === "" || form.commission_value === null || form.commission_value === undefined) {
      toast.error("Commission on Order is mandatory (0 is allowed)");
      return;
    }
    setSubmitting(true);
    try {
      // Coerce numeric fields — empty strings from <input type="number"> would otherwise fail 422
      const payload = {
        ...form,
        // When shipping is marked same as billing, mirror the billing fields
        // into shipping at submit time so downstream consumers (Dispatch,
        // Invoice) always see the four shipping fields populated.
        shipping_address: form.shipping_same_as_billing ? (form.billing_address || "") : form.shipping_address,
        shipping_pincode: form.shipping_same_as_billing ? (form.billing_pincode || "") : form.shipping_pincode,
        shipping_district: form.shipping_same_as_billing ? (form.billing_district || "") : form.shipping_district,
        shipping_state: form.shipping_same_as_billing ? (form.billing_state || "") : form.shipping_state,
        agent_commission_value: parseFloat(form.agent_commission_value) || 0,
        salesmen: (form.salesmen || []).map((s) => ({
          user_id: s.user_id,
          percent: parseFloat(s.percent) || 0,
        })),
      };
      await api.post("/orders", payload);
      toast.success("Order submitted");
      setForm(emptyForm());
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const viewOrder = async (id) => {
    try {
      const { data } = await api.get(`/orders/${id}/full`);
      setViewing(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const [pendingDeleteOrder, setPendingDeleteOrder] = useState(null);
  const deleteOrder = (id) => setPendingDeleteOrder(id);
  const confirmDeleteOrder = async (reason) => {
    try {
      await api.delete(`/orders/${pendingDeleteOrder}`, { data: { reason } });
      toast.success("Order moved to Recycle Bin");
      load();
      setViewing(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); throw e; }
  };

  const cancelOrder = async (id, orderNo) => {
    const reason = window.prompt(`Cancel order ${orderNo}?\n\nEnter the reason for cancellation (required):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A cancellation reason is required"); return; }
    try {
      await api.post(`/orders/${id}/cancel`, { reason: reason.trim() });
      toast.success(`Order ${orderNo} cancelled`);
      load();
      setViewing(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openEdit = (order) => {
    setEditing(order);
    setForm({ ...emptyForm(), ...order });
    setEditRemark("");
  };

  const saveEdit = async () => {
    if (!editRemark.trim()) {
      toast.error("Please add a remark describing what changed and why");
      return;
    }
    try {
      // Mirror billing → shipping when same-as-billing is on (same logic as
      // initial submit) so downstream stays in sync after an edit.
      const payload = {
        ...form,
        shipping_address: form.shipping_same_as_billing ? (form.billing_address || "") : form.shipping_address,
        shipping_pincode: form.shipping_same_as_billing ? (form.billing_pincode || "") : form.shipping_pincode,
        shipping_district: form.shipping_same_as_billing ? (form.billing_district || "") : form.shipping_district,
        shipping_state: form.shipping_same_as_billing ? (form.billing_state || "") : form.shipping_state,
        edit_remark: editRemark.trim(),
      };
      await api.patch(`/orders/${editing.id}`, payload);
      toast.success("Order updated");
      setEditing(null);
      setEditRemark("");
      setViewing(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1500px] space-y-6">
      <Dialog open={open} onOpenChange={setOpen}>
        <PageHeader
          moduleKey="sales_orders"
          tone="indigo"
          icon={ShoppingBag}
          eyebrow="Sales Workflow"
          title="Orders"
          subtitle="Raise orders, generate invoices, and track payments. Master Admin and Accounts Admin can edit, cancel, or override rules."
        />
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading">Raise New Order</DialogTitle>
          </DialogHeader>
          <OrderForm form={form} setForm={setForm} canOverride={canEdit} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="order-submit">
              {submitting ? "Submitting…" : "Submit Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabAccessibleFor(user, "sales_orders", "mine") && (
            <TabsTrigger value="mine" data-testid="order-tab-mine">My Orders</TabsTrigger>
          )}
          {tabAccessibleFor(user, "sales_orders", "mine") && (
            <TabsTrigger value="crafted" data-testid="order-tab-crafted">Crafted Orders</TabsTrigger>
          )}
          {canEdit && tabAccessibleFor(user, "sales_orders", "all") && <TabsTrigger value="all" data-testid="order-tab-all">All Orders</TabsTrigger>}
          {tabAccessibleFor(user, "sales_orders", "invoice") && (
            <TabsTrigger value="invoice" data-testid="order-tab-invoice">Invoice Generation</TabsTrigger>
          )}
          {tabAccessibleFor(user, "sales_orders", "service") && (
            <TabsTrigger value="service" data-testid="order-tab-service">Service Order Management</TabsTrigger>
          )}
        </TabsList>

        {/* Orders tabs (My / All) — share the same table + filter chips */}
        {(tab === "mine" || tab === "crafted" || tab === "all") && (
          <TabsContent value={tab} className="mt-4">
            <OrderFilterChips filters={filters} setFilters={setFilters} activeCount={activeFilterCount} />
            <div className="flex flex-wrap items-end gap-3 mb-4 mt-3">
              <div className="relative w-full sm:w-[320px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order, client, sales associate…" className="pl-9 h-10" data-testid="order-search" />
              </div>
              <Input
                value={filters.sales_associate}
                onChange={(e) => setFilters({ ...filters, sales_associate: e.target.value })}
                placeholder="Sales Associate…"
                className="h-10 text-xs w-[180px]"
                data-testid="order-filter-sa"
              />
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-10 text-xs w-[150px]" data-testid="order-filter-from" title="From date" />
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-10 text-xs w-[150px]" data-testid="order-filter-to" title="To date" />
              <Button className="h-10" onClick={() => { if (form.quote_request_id) setForm(emptyForm()); setOpen(true); }} data-testid="order-new-button">
                <Plus size={16} className="mr-1.5" /> New Order
              </Button>
              <div className="ml-auto text-[11px] text-slate-500 self-center">
                Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of {orders.length}
              </div>
            </div>
          {viewMode === "card" ? (
            filtered.length === 0 ? (
              <div className="pk-card p-12 text-center text-slate-400">No orders yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="order-cards">
                {filtered.map((o) => (
                  <div key={o.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`order-card-${o.order_no}`} data-highlight-id={o.id}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="mono text-[11px] font-bold text-slate-700 truncate flex items-center gap-1.5">{o.order_no} <OrderTypeTag type={o.order_type} testid={`order-type-card-${o.order_no}`} /></div>
                        <div className="font-bold text-slate-900 truncate mt-0.5">{o.client_name}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">{o.product_service}</div>
                      </div>
                      {o.is_gem_contract && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] shrink-0">GeM</Badge>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Total</div><div className="mono font-semibold text-slate-900">₹ {o.total_value}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Created By</div><div className="text-slate-800 truncate">{o.created_by_name}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Order</div><Badge className={(ORDER_STATUS_COLOR[o.status] || "bg-slate-100 text-slate-700") + " text-[10px]"}>{o.status}</Badge></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Payment</div><Badge className={(PAY_COLOR[o.payment_status] || "bg-slate-100") + " text-[10px]"}>{o.payment_status}</Badge></div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 flex justify-end">
                      <Button size="sm" variant="outline" onClick={() => viewOrder(o.id)} className="h-7 text-[10.5px]" data-testid={`order-view-card-${o.order_no}`}>
                        <Eye size={11} className="mr-1" /> View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
          <div className="pk-card overflow-x-auto">
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <Th>Order No</Th><Th>Client</Th><Th>Total</Th><Th>GeM</Th>
                  <Th>Order Status</Th><Th>Payment</Th><Th>Created By</Th><Th className="text-right">View</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-400">No orders yet.</td></tr>}
                {filtered.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`order-row-${o.order_no}`} data-highlight-id={o.id}>
                    <td className="px-4 py-3 mono font-semibold text-slate-900" data-label="Order No"><span className="inline-flex items-center gap-1.5">{o.order_no} <OrderTypeTag type={o.order_type} testid={`order-type-${o.order_no}`} /></span></td>
                    <td className="px-4 py-3" data-label="Client">
                      <div className="font-medium text-slate-900">{o.client_name}</div>
                      <div className="text-[10px] text-slate-500">{o.product_service}</div>
                    </td>
                    <td className="px-4 py-3 mono" data-label="Total">₹ {o.total_value}</td>
                    <td className="px-4 py-3" data-label="GeM">{o.is_gem_contract ? <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">GeM</Badge> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3" data-label="Status"><Badge className={ORDER_STATUS_COLOR[o.status] || "bg-slate-100 text-slate-700"}>{o.status}</Badge></td>
                    <td className="px-4 py-3" data-label="Payment"><Badge className={PAY_COLOR[o.payment_status] || "bg-slate-100"}>{o.payment_status}</Badge></td>
                    <td className="px-4 py-3 text-[11px] text-slate-600" data-label="Created By">{o.created_by_name}</td>
                    <td className="px-4 py-3 text-right" data-label="">
                      <Button size="sm" variant="outline" onClick={() => viewOrder(o.id)} className="h-8 text-xs" data-testid={`order-view-${o.order_no}`}>
                        <Eye size={12} className="mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </TabsContent>
        )}

        {/* Invoice Generation — merged sub-module */}
        <TabsContent value="invoice" className="mt-4" data-testid="order-invoice-tabcontent">
          <InvoiceWorkflow embedded />
        </TabsContent>

        {/* Service Order Management — merged sub-module */}
        <TabsContent value="service" className="mt-4" data-testid="order-service-tabcontent">
          <ServiceOrderManagement embedded />
        </TabsContent>
      </Tabs>

      <OrderDetailDialog viewing={viewing} onClose={() => setViewing(null)} isMaster={isMaster} canEdit={canEdit} currentUserId={user?.id} onDelete={deleteOrder} onCancel={cancelOrder} onEdit={openEdit} />

      {/* Edit Dialog — Master Admin, Accounts Admin, or Order Creator (mandatory edit-remark). Backend rejects if an invoice already exists. */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditRemark(""); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading">Edit Order — {editing?.order_no}</DialogTitle>
            <div className="text-[11px] text-slate-500">Editing as <span className="font-semibold">{isMaster ? "Master Admin" : (user?.role === "accounts_admin" ? "Accounts Admin" : "Order Creator")}</span>. A remark is required.</div>
          </DialogHeader>
          <OrderForm form={form} setForm={setForm} canOverride={canEdit} />
          <div className="border-t border-slate-200 pt-3 mt-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">Reason for change *</Label>
            <Textarea rows={2} value={editRemark} onChange={(e) => setEditRemark(e.target.value)} placeholder="Describe what was changed and why (audit trail)" className="mt-1.5" data-testid="order-edit-remark" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setEditRemark(""); }}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="order-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteWithReasonDialog
        open={!!pendingDeleteOrder}
        onClose={() => setPendingDeleteOrder(null)}
        title="Delete this order?"
        description="Master Admin only — the order and all linked invoice/dispatch records will move to Admin Hub → Recycle Bin. Master Admin can restore it later."
        onConfirm={confirmDeleteOrder}
        testid="order-delete"
      />
    </div>
  );
}

function formFromQuote(q) {
  const num = (v) => String(v ?? "").replace(/[^0-9.]/g, "");
  const comm = String(q.commission ?? "");
  return {
    ...emptyForm(),
    quote_request_id: q.id,
    client_id: q.client_id || "",
    client_name: q.client_name || "",
    total_value: num(q.order_value),
    commission_type: comm.includes("%") ? "percent" : "amount",
    commission_value: num(comm),
    remark: [`Created from approved quote request (${q.client_name || ""})`, q.important_remark].filter(Boolean).join(" — "),
  };
}

export function emptyForm() {
  return {
    client_name: "", client_id: "", invoice_company_name: "", order_type: "Product",
    allow_multiple_invoices: false,
    billing_address: "", billing_pincode: "", billing_district: "", billing_state: "",
    shipping_same_as_billing: true, shipping_address: "", shipping_pincode: "", shipping_district: "", shipping_state: "",
    contact_name: "", contact_number: "",
    is_gem_contract: false, gem_contract_no: "", gem_contract_date: "", gem_last_delivery_date: "",
    sale_associate_name: "", sale_associate_id: "",
    commission_type: "amount", commission_value: "", commission: "",
    products: [], product_service: "", total_value: "", remark: "",
    has_po: false, po_number: "", po_date: "", po_url: "",
    salesmen: [], // [{user_id, percent, amount?}]
    is_agent_based: false, agent_name: "", agent_commission_type: "percent", agent_commission_value: "",
  };
}

export function OrderForm({ form, setForm, canOverride }) {
  const [people, setPeople] = useState([]);
  const [clientResults, setClientResults] = useState([]);
  const [clientQuery, setClientQuery] = useState(form.client_name || "");
  const [showClientDrop, setShowClientDrop] = useState(false);
  const [invoiceCompanies, setInvoiceCompanies] = useState([]);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [clientContext, setClientContext] = useState(null); // {contacts, assigned_to_id, assigned_to_name}
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", number: "", type: "", type_other: "" });
  const [newClientOpen, setNewClientOpen] = useState(false);

  // Mirror the CONTACT_TYPES used in the New Client form (ClientFormDialog).
  const CONTACT_TYPES = ["Chairman", "Principal", "School Teacher", "ATL In-Charge", "Other"];

  useEffect(() => { api.get("/directory").then(({ data }) => setPeople(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/invoice-companies").then(({ data }) => setInvoiceCompanies(data || [])).catch(() => {});
  }, []);

  // When a client is picked, fetch the order-context (contacts + assigned associate + full address).
  useEffect(() => {
    if (!form.client_id) { setClientContext(null); return; }
    api.get(`/clients/${form.client_id}/order-context`).then(({ data }) => {
      setClientContext(data);
      // Auto-fill sales associate + address fields from the client where the
      // order form currently has blanks. Existing values on the form take
      // precedence (so a manual override is never overwritten).
      setForm((f) => {
        const defaultAssocId = f.sale_associate_id || data.assigned_to_id || "";
        // Pre-seed the Sales Team multi-select with the default associate
        // (only if the team is still empty — never overwrite manual picks).
        let nextSalesmen = f.salesmen || [];
        if ((nextSalesmen.length === 0) && defaultAssocId) {
          nextSalesmen = [{ user_id: defaultAssocId, percent: 100 }];
        }
        return ({
          ...f,
          sale_associate_name: f.sale_associate_name || data.assigned_to_name || "",
          sale_associate_id: defaultAssocId,
          salesmen: nextSalesmen,
          // Billing address now holds ONLY the street; district and state are
          // separate fields below.
          billing_address: f.billing_address || data.address_raw || "",
          billing_pincode: f.billing_pincode || data.pincode || "",
          billing_district: f.billing_district || data.district || "",
          billing_state: f.billing_state || data.state || "",
          shipping_address: f.shipping_same_as_billing
            ? (f.billing_address || data.address_raw || "")
            : (f.shipping_address || data.address_raw || ""),
          shipping_pincode: f.shipping_same_as_billing
            ? (f.billing_pincode || data.pincode || "")
            : (f.shipping_pincode || data.pincode || ""),
        });
      });
    }).catch(() => setClientContext(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id]);

  const addInvoiceCompany = async () => {
    const name = newCompanyName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/invoice-companies", { name });
      setInvoiceCompanies((prev) => prev.find((c) => c.id === data.id) ? prev : [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ ...form, invoice_company_name: data.name });
      setNewCompanyName(""); setShowNewCompany(false);
      toast.success(`Added "${data.name}" to the invoice company list`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addClientContact = async () => {
    if (!form.client_id) { toast.error("Pick a client first"); return; }
    const number = (newContact.number || "").trim();
    if (!number) { toast.error("Contact number is required"); return; }
    const effectiveType = newContact.type === "Other"
      ? (newContact.type_other || "").trim()
      : (newContact.type || "").trim();
    if (newContact.type === "Other" && !effectiveType) {
      toast.error("Please describe the 'Other' contact type");
      return;
    }
    try {
      const { data } = await api.post(`/clients/${form.client_id}/contacts`, {
        name: newContact.name,
        number,
        email: newContact.email || "",
        role: effectiveType,  // backend stores this under `type`
      });
      setClientContext((ctx) => ({ ...(ctx || {}), contacts: data.contacts }));
      setForm({ ...form, contact_name: newContact.name, contact_number: number });
      setNewContact({ name: "", email: "", number: "", type: "", type_other: "" });
      setShowNewContact(false);
      toast.success("Contact added to client database");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const setSalesAssociate = async (employeeId) => {
    const emp = people.find((p) => p.id === employeeId);
    if (!emp) return;
    try {
      await api.post(`/clients/${form.client_id}/set-sales-associate`, { employee_id: employeeId });
      setForm((f) => {
        // Replace any unpicked salesmen with this default one (only if team is empty)
        const team = (f.salesmen && f.salesmen.length > 0) ? f.salesmen : [{ user_id: employeeId, percent: 100 }];
        return { ...f, sale_associate_name: emp.name, sale_associate_id: employeeId, salesmen: team };
      });
      setClientContext((ctx) => ({ ...(ctx || {}), assigned_to_id: employeeId, assigned_to_name: emp.name }));
      toast.success(`${emp.name} mapped as sales associate for this client`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Products list: backward-compat with old single product_service string.
  const productList = form.products && form.products.length > 0
    ? form.products
    : (form.product_service ? [{ name: form.product_service, description: "" }] : [{ name: "", description: "" }]);
  const setProductList = (newList) => {
    setForm({
      ...form,
      products: newList,
      // Keep product_service in sync as a comma-joined string for legacy consumers.
      product_service: newList.filter((p) => p.name).map((p) => p.name).join(", "),
    });
  };
  const addProductRow = () => setProductList([...productList, { name: "", description: "" }]);
  const removeProductRow = (idx) => {
    const next = productList.filter((_, i) => i !== idx);
    setProductList(next.length === 0 ? [{ name: "", description: "" }] : next);
  };
  const updateProductRow = (idx, key, val) => {
    setProductList(productList.map((p, i) => i === idx ? { ...p, [key]: val } : p));
  };

  // Debounced client search — fetch on every keystroke (incl. empty → top 20)
  useEffect(() => {
    const q = clientQuery.trim();
    const id = setTimeout(() => {
      api.get(`/clients/search?q=${encodeURIComponent(q)}`)
        .then(({ data }) => setClientResults(data || []))
        .catch(() => setClientResults([]));
    }, 180);
    return () => clearTimeout(id);
  }, [clientQuery]);

  const pickClient = (c) => {
    setForm({
      ...form,
      client_id: c.id,
      client_name: c.client_name,
      // Always pull the picked client's address fields — overwriting any
      // earlier (possibly stale) values. The user can still edit them inline.
      billing_address: (c.address || "").trim(),
      billing_pincode: (c.pincode || "").trim(),
      billing_district: (c.district || "").trim(),
      billing_state: (c.state || "").trim(),
      // Reset contact + sales associate so the new client's data takes over.
      contact_name: "", contact_number: "",
      sale_associate_name: "", sale_associate_id: "",
    });
    setClientQuery(c.client_name);
    setShowClientDrop(false);
  };

  const totalNum = parseFloat(String(form.total_value).replace(/[^0-9.]/g, "")) || 0;
  const sumPct = (form.salesmen || []).reduce((s, x) => s + (parseFloat(x.percent) || 0), 0);
  const pctOk = (form.salesmen || []).length === 0 || Math.abs(sumPct - 100) < 0.5;

  const addSalesman = (uid) => {
    if (!uid || (form.salesmen || []).some((s) => s.user_id === uid)) return;
    const arr = [...(form.salesmen || []), { user_id: uid, percent: 0 }];
    // Default equal split when adding
    const each = +(100 / arr.length).toFixed(2);
    arr.forEach((s) => { s.percent = each; });
    setForm({ ...form, salesmen: arr });
  };
  const removeSalesman = (uid) => {
    const arr = (form.salesmen || []).filter((s) => s.user_id !== uid);
    if (arr.length > 0) {
      const each = +(100 / arr.length).toFixed(2);
      arr.forEach((s) => { s.percent = each; });
    }
    setForm({ ...form, salesmen: arr });
  };
  const setPct = (uid, val) => {
    const arr = (form.salesmen || []).map((s) => s.user_id === uid ? { ...s, percent: val } : s);
    setForm({ ...form, salesmen: arr });
  };
  const personName = (uid) => people.find((p) => p.id === uid)?.name || "Unknown";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
      <F label="Client / School Name *">
        <div className="relative">
          <div className="flex items-center gap-2">
            <Input
              value={clientQuery}
              onChange={(e) => {
                setClientQuery(e.target.value);
                setForm({ ...form, client_name: e.target.value, client_id: "" });
                setShowClientDrop(true);
              }}
              onFocus={() => setShowClientDrop(true)}
              onBlur={() => setTimeout(() => setShowClientDrop(false), 250)}
              placeholder="Type to search CRM — or pick Other to enter free text"
              data-testid="of-client"
              autoComplete="off"
              className="flex-1"
            />
            {form.client_id ? (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 whitespace-nowrap" data-testid="of-client-from-crm">From CRM ✓</span>
            ) : clientQuery ? (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-200 text-slate-700 whitespace-nowrap" data-testid="of-client-custom">Custom</span>
            ) : null}
          </div>
          {showClientDrop && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto" data-testid="of-client-suggestions">
              {clientResults.length === 0 && clientQuery.trim() && (
                <div className="px-3 py-2 text-[11px] text-slate-500 italic">No CRM match for &quot;{clientQuery}&quot;</div>
              )}
              {clientResults.map((c) => (
                <button
                  type="button" key={c.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickClient(c)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100"
                  data-testid={`of-client-pick-${c.id}`}
                >
                  <div className="text-sm font-medium text-slate-900">{c.client_name}</div>
                  <div className="text-[10px] text-slate-500 mono flex items-center gap-2 flex-wrap">
                    {c.u_dise && <span>{c.u_dise}</span>}
                    {c.client_type && <span>· {c.client_type}</span>}
                    {(c.district || c.state) && <span>· {[c.district, c.state].filter(Boolean).join(", ")}</span>}
                  </div>
                </button>
              ))}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setShowClientDrop(false); setNewClientOpen(true); }}
                className="w-full text-left px-3 py-2.5 bg-amber-50 hover:bg-amber-100 border-t-2 border-amber-200 text-amber-900 font-semibold text-sm flex items-center gap-2"
                data-testid="of-client-other"
              >
                <span className="text-base">＋</span> Other — Add New Client / School
              </button>
            </div>
          )}
        </div>
      </F>

      {/* Full New-Client dialog (same component as CRM Reporting). The new
          client lands in Pending Approval (is_approved=false) and won't show
          up in the order-form search until a master/admin approves it. */}
      <ClientFormDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        title="New Client (Pending Approval)"
        submitLabel="Submit for Approval"
        onSaved={() => {
          toast.success("New client submitted for approval. Once approved, search the client again to attach this order.");
          // Clear the client search so the user doesn't try to use the unapproved one.
          setForm({ ...form, client_id: "", client_name: "" });
          setClientQuery("");
        }}
      />
      <F label="Invoice Company Name">
        <div className="flex items-center gap-1.5">
          <select
            value={form.invoice_company_name}
            onChange={(e) => {
              if (e.target.value === "__add__") { setShowNewCompany(true); return; }
              setForm({ ...form, invoice_company_name: e.target.value });
            }}
            className="flex-1 h-10 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3"
            data-testid="of-invoice-company">
            <option value="">— Leave blank (Accounts will fill in) —</option>
            {invoiceCompanies.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
            <option value="__add__">+ Add new company…</option>
          </select>
        </div>
        {showNewCompany && (
          <div className="mt-1.5 flex items-center gap-1.5" data-testid="of-new-company-row">
            <Input
              autoFocus
              placeholder="New company name (e.g. Vidyut)"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              className="h-9 text-sm"
              data-testid="of-new-company-input"
            />
            <Button type="button" size="sm" onClick={addInvoiceCompany} className="h-9 text-[11px]" data-testid="of-new-company-save">Add</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setShowNewCompany(false); setNewCompanyName(""); }} className="h-9 text-[11px]">Cancel</Button>
          </div>
        )}
        {!form.invoice_company_name && !showNewCompany && (
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
            Tip: Leave it blank if unsure — the Accounts team will pick the right legal entity.
          </div>
        )}
      </F>

      <div className="md:col-span-2">
        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Order Type *</Label>
        <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="of-order-type-group">
          <button type="button" onClick={() => setForm({ ...form, order_type: "Product" })} data-testid="of-order-type-product"
            className={`border rounded-md p-3 text-left transition-all ${form.order_type === "Product" ? "border-[var(--pk-accent)] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="text-sm font-semibold text-slate-900">Product</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Single invoice will be generated for this order.</div>
          </button>
          <button type="button" onClick={() => setForm({ ...form, order_type: "Service" })} data-testid="of-order-type-service"
            className={`border rounded-md p-3 text-left transition-all ${form.order_type === "Service" ? "border-[var(--pk-accent)] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="text-sm font-semibold text-slate-900">Service</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Accounts can raise multiple invoices as work progresses.</div>
          </button>
        </div>
      </div>

      {canOverride && (
        <div className="md:col-span-2 flex items-center gap-3 px-3 py-2.5 bg-amber-50/60 border border-amber-200 rounded-md" data-testid="of-multi-invoice-wrap">
          <Switch checked={!!form.allow_multiple_invoices} onCheckedChange={(v) => setForm({ ...form, allow_multiple_invoices: v })} data-testid="of-allow-multi-invoice" />
          <div>
            <div className="text-sm font-semibold text-slate-800">Allow multiple invoices on this order</div>
            <div className="text-[11px] text-slate-600">Master / Accounts override — applies to both Product and Service orders.</div>
          </div>
        </div>
      )}

      {/* Contact selector — picks from client's saved contacts; allows adding new (also saves to client DB). */}
      <F label="Contact (for dispatch)" full>
        {form.client_id ? (
          <div className="space-y-1.5">
            <select
              value={form.contact_number}
              onChange={(e) => {
                if (e.target.value === "__add__") { setShowNewContact(true); return; }
                const ct = (clientContext?.contacts || []).find((c) => c.number === e.target.value);
                if (ct) setForm({ ...form, contact_name: ct.name || "", contact_number: ct.number });
              }}
              className="w-full h-10 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3"
              data-testid="of-contact-picker">
              <option value="">— Select a contact —</option>
              {(clientContext?.contacts || []).map((c) => (
                <option key={c.number} value={c.number}>
                  {c.name ? `${c.name} · ` : ""}{c.number}{c.role ? ` (${c.role})` : ""}
                </option>
              ))}
              <option value="__add__">+ Add new contact…</option>
            </select>
            {showNewContact && (
              <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/60 dark:bg-slate-800/40 space-y-2" data-testid="of-new-contact-row">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">New contact (will be saved to this client&apos;s database)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input placeholder="Contact name *"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    className="h-9 text-sm" data-testid="of-new-contact-name" />
                  <Input placeholder="Email"
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    className="h-9 text-sm" data-testid="of-new-contact-email" />
                  <Input placeholder="Number *"
                    value={newContact.number}
                    onChange={(e) => setNewContact({ ...newContact, number: e.target.value })}
                    className="h-9 text-sm" data-testid="of-new-contact-number" />
                  <select
                    value={newContact.type}
                    onChange={(e) => setNewContact({ ...newContact, type: e.target.value, type_other: e.target.value === "Other" ? newContact.type_other : "" })}
                    className="h-9 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3"
                    data-testid="of-new-contact-type">
                    <option value="">— Contact type —</option>
                    {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {newContact.type === "Other" && (
                    <Input placeholder="Specify other type *"
                      value={newContact.type_other}
                      onChange={(e) => setNewContact({ ...newContact, type_other: e.target.value })}
                      className="h-9 text-sm md:col-span-2" data-testid="of-new-contact-type-other" />
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button type="button" size="sm" onClick={addClientContact} className="h-8 text-[11px] bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="of-new-contact-save">
                    Add to Client DB
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => { setShowNewContact(false); setNewContact({ name: "", email: "", number: "", type: "", type_other: "" }); }}
                    className="h-8 text-[11px]">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {form.contact_number && !showNewContact && (
              <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5" data-testid="of-contact-summary">
                Using <b>{form.contact_name || "(no name)"}</b> · <span className="mono">{form.contact_number}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 italic px-2 py-2 bg-slate-50 dark:bg-slate-800/40 rounded-md">
            Pick a client first — their saved contacts will appear here.
          </div>
        )}
      </F>

      {/* Sales Associate — auto-filled from client. If client has none, show a non-temp employee picker. */}
      <F label="Sales Associate (for this Client)" full>
        {form.client_id ? (
          (clientContext?.assigned_to_id || form.sale_associate_id) ? (
            <div className="flex items-center justify-between gap-2 bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800 rounded-md px-3 py-2" data-testid="of-sales-associate-assigned">
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{form.sale_associate_name || clientContext?.assigned_to_name}</div>
                <div className="text-[10.5px] text-slate-500">Auto-fetched from this client&apos;s CRM record</div>
              </div>
              <span className="text-[9.5px] font-bold bg-emerald-200/70 dark:bg-emerald-900/50 text-emerald-900 dark:text-emerald-200 rounded-full px-2 py-0.5">Linked</span>
            </div>
          ) : (
            <div className="space-y-1">
              <select
                value={form.sale_associate_id || ""}
                onChange={(e) => setSalesAssociate(e.target.value)}
                className="w-full h-10 text-sm border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/15 rounded-md px-3"
                data-testid="of-sales-associate-picker">
                <option value="">— Assign a sales associate to this client —</option>
                {people.filter((p) => p.is_active !== false).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.department ? ` · ${p.department}` : ""}</option>
                ))}
              </select>
              <div className="text-[10.5px] text-amber-800 dark:text-amber-300">
                No sales associate is mapped to this client yet. Pick one — they&apos;ll be saved against the client for future orders.
              </div>
            </div>
          )
        ) : (
          <div className="text-[11px] text-slate-500 italic px-2 py-2 bg-slate-50 dark:bg-slate-800/40 rounded-md">
            Pick a client first.
          </div>
        )}
      </F>

      <F label="Billing Address *" full>
        <Textarea rows={2} value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="of-billing-addr" placeholder="Street / house no / landmark" />
        {form.client_id && (
          <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1">
            {clientContext?.address_raw
              ? <>Auto-fetched from this client. Editing here won&apos;t overwrite the client record.</>
              : <>This client has no address on file — what you enter here will also be saved to the client database.</>}
          </div>
        )}
      </F>
      <F label="Billing Pincode *">
        <Input value={form.billing_pincode} onChange={(e) => setForm({ ...form, billing_pincode: e.target.value })} required data-testid="of-billing-pin" />
      </F>
      <F label="District *">
        <Input value={form.billing_district} onChange={(e) => setForm({ ...form, billing_district: e.target.value })} required data-testid="of-billing-district" placeholder="e.g. Bengaluru Urban" />
      </F>
      <F label="State *">
        <select
          value={form.billing_state}
          onChange={(e) => setForm({ ...form, billing_state: e.target.value })}
          required
          className="h-10 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3 w-full"
          data-testid="of-billing-state"
        >
          <option value="">— Select State / UT —</option>
          {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </F>
      {/* Explicit "Update in Client DB" — visible to any user with order
          access. Server-side it is restricted to Master / Accounts / CRM. */}
      {form.client_id && (() => {
        const drift = (
          (form.billing_address || "").trim() !== (clientContext?.address_raw || "").trim()
          || (form.billing_pincode || "").trim() !== (clientContext?.pincode || "").trim()
          || (form.billing_district || "").trim() !== (clientContext?.district || "").trim()
          || (form.billing_state || "").trim() !== (clientContext?.state || "").trim()
        );
        if (!drift) return null;
        return (
          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-3 flex-wrap rounded-md px-3 py-2 border border-amber-200 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/20">
              <div className="text-[11.5px] text-amber-900 dark:text-amber-200">
                These billing details differ from the client record on file. Click to push them back to the client database (audited).
              </div>
              <Button
                type="button" size="sm"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/clients/${form.client_id}/sync-address`, {
                      address: form.billing_address,
                      pincode: form.billing_pincode,
                      district: form.billing_district,
                      state: form.billing_state,
                      source: "order-form",
                    });
                    if (data.updated === 0) {
                      toast.info(data.message || "Client DB already has these values");
                    } else {
                      toast.success(`Client DB updated (${data.updated} field${data.updated === 1 ? "" : "s"})`);
                    }
                    // refresh clientContext snapshot
                    const { data: ctx } = await api.get(`/clients/${form.client_id}/order-context`);
                    setClientContext(ctx);
                  } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                }}
                className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="of-sync-client-db">
                Update in Client DB
              </Button>
            </div>
          </div>
        );
      })()}

      <div className="md:col-span-2 flex items-center gap-3 px-1 py-1">
        <Switch
          checked={form.shipping_same_as_billing}
          onCheckedChange={(v) => {
            // When the user flips ON "same as billing", mirror the four
            // billing fields into the shipping fields so downstream consumers
            // (Dispatch, Invoice) always have a populated shipping address
            // even if the user never opens that section.
            if (v) {
              setForm({
                ...form,
                shipping_same_as_billing: true,
                shipping_address: form.billing_address || "",
                shipping_pincode: form.billing_pincode || "",
                shipping_district: form.billing_district || "",
                shipping_state: form.billing_state || "",
              });
            } else {
              setForm({ ...form, shipping_same_as_billing: false });
            }
          }}
          data-testid="of-same-shipping"
        />
        <span className="text-sm text-slate-700">Shipping address same as billing</span>
      </div>
      {!form.shipping_same_as_billing && (
        <>
          <F label="Shipping Address *" full>
            <Textarea rows={2} value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} required data-testid="of-shipping-addr" />
          </F>
          <F label="Shipping Pincode *">
            <Input value={form.shipping_pincode} onChange={(e) => setForm({ ...form, shipping_pincode: e.target.value })} required data-testid="of-shipping-pin" />
          </F>
          <F label="Shipping District *">
            <Input value={form.shipping_district} onChange={(e) => setForm({ ...form, shipping_district: e.target.value })} required data-testid="of-shipping-district" placeholder="e.g. Bengaluru Urban" />
          </F>
          <F label="Shipping State *">
            <select
              value={form.shipping_state}
              onChange={(e) => setForm({ ...form, shipping_state: e.target.value })}
              required
              className="h-10 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3 w-full"
              data-testid="of-shipping-state"
            >
              <option value="">— Select State / UT —</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </F>
        </>
      )}

      <div className="md:col-span-2 flex items-center gap-3 px-1 py-1 border-t border-slate-100 pt-3 mt-1">
        <Switch checked={form.is_gem_contract} onCheckedChange={(v) => setForm({ ...form, is_gem_contract: v })} data-testid="of-is-gem" />
        <span className="text-sm font-semibold text-slate-800">Is this a GeM contract?</span>
      </div>
      {form.is_gem_contract && (
        <>
          <F label="GeM Contract No."><Input value={form.gem_contract_no} onChange={(e) => setForm({ ...form, gem_contract_no: e.target.value })} data-testid="of-gem-no" /></F>
          <F label="Contract Date"><Input type="date" value={form.gem_contract_date} onChange={(e) => setForm({ ...form, gem_contract_date: e.target.value })} data-testid="of-gem-date" /></F>
          <F label="Last Delivery Date"><Input type="date" value={form.gem_last_delivery_date} onChange={(e) => setForm({ ...form, gem_last_delivery_date: e.target.value })} data-testid="of-gem-last" /></F>
        </>
      )}

      {/* Products / Services — repeatable rows with description */}
      <div className="md:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3 mt-1">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Products / Services *</Label>
          <Button type="button" size="sm" variant="outline" onClick={addProductRow} className="h-7 text-[11px]" data-testid="of-add-product">+ Add Product / Service</Button>
        </div>
        <div className="space-y-2" data-testid="of-product-list">
          {productList.map((p, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start" data-testid={`of-product-row-${idx}`}>
              <Input
                value={p.name}
                onChange={(e) => updateProductRow(idx, "name", e.target.value)}
                placeholder="Product / Service name"
                className="h-10"
                data-testid={`of-product-name-${idx}`}
              />
              <Textarea
                value={p.description}
                onChange={(e) => updateProductRow(idx, "description", e.target.value)}
                placeholder="Description (specs, quantity, notes…)"
                rows={2}
                className="text-sm"
                data-testid={`of-product-desc-${idx}`}
              />
              {productList.length > 1 && (
                <Button type="button" size="sm" variant="outline" onClick={() => removeProductRow(idx)} className="h-10 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`of-product-remove-${idx}`}>
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <F label="Total Order Value (INR) *" required>
        <Input
          type="number" step="0.01" min="0"
          value={form.total_value}
          onChange={(e) => setForm({ ...form, total_value: e.target.value })}
          required
          placeholder="e.g. 250000"
          data-testid="of-total"
        />
      </F>

      {/* Commission — split into amount input + % / ₹ toggle (mandatory) */}
      <F label="Commission on Order *" required>
        <div className="flex items-stretch gap-1.5">
          <Input
            type="number" step="0.01" min="0"
            value={form.commission_value || ""}
            onChange={(e) => {
              const v = e.target.value;
              const t = form.commission_type || "amount";
              setForm({
                ...form,
                commission_value: v,
                commission: v ? `${v}${t === "percent" ? "%" : ""}` : "",
              });
            }}
            placeholder="e.g. 5 or 7500"
            required
            className="flex-1"
            data-testid="of-commission-value"
          />
          <div className="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-800 rounded-md p-0.5 border border-slate-200 dark:border-slate-700" data-testid="of-commission-type-group">
            {[{ k: "amount", l: "₹", tone: "bg-blue-600 text-white" },
              { k: "percent", l: "%", tone: "bg-rose-600 text-white" }].map((opt) => {
              const active = (form.commission_type || "amount") === opt.k;
              return (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => {
                    const v = form.commission_value || "";
                    setForm({
                      ...form,
                      commission_type: opt.k,
                      commission: v ? `${v}${opt.k === "percent" ? "%" : ""}` : "",
                    });
                  }}
                  className={`min-w-[40px] h-8 px-2 text-[13px] font-extrabold rounded-md transition-colors ${
                    active
                      ? `${opt.tone} shadow-md ring-2 ring-offset-1 ${opt.k === "amount" ? "ring-blue-300" : "ring-rose-300"} dark:ring-offset-slate-900`
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                  }`}
                  data-testid={`of-commission-type-${opt.k}`}
                >
                  {opt.l}
                </button>
              );
            })}
          </div>
        </div>
      </F>

      <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="of-remark" /></F>

      <div className="md:col-span-2 flex items-center gap-3 px-1 py-1 border-t border-slate-100 pt-3 mt-1">
        <Switch checked={form.has_po} onCheckedChange={(v) => setForm({ ...form, has_po: v })} data-testid="of-has-po" />
        <span className="text-sm font-semibold text-slate-800">Has Order Confirmation / PO?</span>
      </div>
      {form.has_po && (
        <>
          <F label="PO Number"><Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="of-po-number" /></F>
          <F label="PO Date"><Input type="date" value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} data-testid="of-po-date" /></F>
          <F label="PO Document URL (optional)" full><Input value={form.po_url} onChange={(e) => setForm({ ...form, po_url: e.target.value })} placeholder="Paste Google Drive / OneDrive link" data-testid="of-po-url" /></F>
        </>
      )}

      {/* ===== Sales Attribution (Multi-Salesman) ===== */}
      <div className="md:col-span-2 mt-2">
        <div className="rounded-xl border border-indigo-200/70 dark:border-indigo-900/60 bg-gradient-to-br from-indigo-50/70 via-white to-blue-50/40 dark:from-indigo-950/30 dark:via-[hsl(var(--card))] dark:to-blue-950/20 p-4" data-keep-color>
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                <Users size={13} /> Sales Team — credit one or more associates
              </Label>
              <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
                Add every associate involved. Each gets an equal share by default — tweak the % to taste. Credit stays
                <span className="font-semibold text-amber-700 dark:text-amber-300"> pending </span>
                until Master Admin approves it.
              </div>
            </div>
            {(form.salesmen || []).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-[11px] mono font-bold px-2.5 py-1 rounded-full ${
                    pctOk
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 animate-pulse"
                  }`}
                  data-keep-color
                  data-testid="of-salesmen-pct-sum"
                >
                  Total {sumPct.toFixed(2)}% {pctOk ? "✓" : "(must be 100%)"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const arr = [...(form.salesmen || [])];
                    if (!arr.length) return;
                    const each = +(100 / arr.length).toFixed(2);
                    arr.forEach((s) => { s.percent = each; });
                    setForm({ ...form, salesmen: arr });
                  }}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 transition-all"
                  data-keep-color
                  data-testid="of-salesmen-equal-split"
                >
                  Equal Split
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              onChange={(e) => { addSalesman(e.target.value); e.target.value = ""; }}
              className="h-9 text-sm rounded-md px-3 bg-white dark:bg-[hsl(var(--card))] border border-indigo-200 dark:border-indigo-800 text-slate-800 dark:text-slate-100 font-medium hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40"
              data-testid="of-salesman-add"
            >
              <option value="">+ Add associate…</option>
              {people.filter((p) => !(form.salesmen || []).some((s) => s.user_id === p.id)).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.employee_code ? ` · ${p.employee_code}` : ""}{p.department ? ` · ${p.department}` : ""}
                </option>
              ))}
            </select>
          </div>

          {(form.salesmen || []).length === 0 && (
            <div className="text-[11.5px] italic text-slate-500 dark:text-slate-400 px-3 py-3 bg-white/60 dark:bg-[hsl(var(--muted))] rounded-md border border-dashed border-indigo-200 dark:border-indigo-900">
              No associates tagged yet. Add one or more to attribute this sale and split the commission %.
            </div>
          )}

          <div className="space-y-1.5">
            {(form.salesmen || []).map((s, idx) => {
              const amt = totalNum * (parseFloat(s.percent) || 0) / 100;
              return (
                <div
                  key={s.user_id}
                  className="flex items-center gap-2 bg-white dark:bg-[hsl(var(--card))] border border-indigo-100 dark:border-indigo-900/60 rounded-md px-2.5 py-1.5"
                  data-testid={`of-salesman-row-${s.user_id}`}
                >
                  <span className="w-6 h-6 grid place-items-center rounded-full bg-indigo-600 text-white text-[11px] font-extrabold shrink-0" data-keep-color>{idx + 1}</span>
                  <span className="text-sm font-semibold flex-1 truncate text-slate-800 dark:text-slate-100">{personName(s.user_id)}</span>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={s.percent}
                      onChange={(e) => setPct(s.user_id, parseFloat(e.target.value) || 0)}
                      className="h-8 w-20 text-xs text-center"
                      data-testid={`of-salesman-pct-${s.user_id}`}
                    />
                    <span className="text-[11px] mono text-slate-500 dark:text-slate-400">%</span>
                  </div>
                  <span className="text-[11.5px] mono font-bold text-emerald-700 dark:text-emerald-300 w-28 text-right shrink-0">
                    ₹ {amt.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeSalesman(s.user_id)}
                    className="h-8 w-8 p-0 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-900 hover:bg-rose-50 dark:hover:bg-rose-900/30 shrink-0"
                    data-testid={`of-salesman-remove-${s.user_id}`}
                  >
                    <X size={12} />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Agent-based Sale Toggle ===== */}
      <div className="md:col-span-2 flex items-center gap-3 px-1 py-1 border-t border-slate-100 pt-3 mt-1">
        <Switch checked={!!form.is_agent_based} onCheckedChange={(v) => setForm({ ...form, is_agent_based: v })} data-testid="of-is-agent" />
        <span className="text-sm font-semibold text-slate-800">Is this an agent-based sale?</span>
      </div>
      {form.is_agent_based && (
        <>
          <F label="Agent Name *"><Input value={form.agent_name} onChange={(e) => setForm({ ...form, agent_name: e.target.value })} placeholder="External agent / broker" data-testid="of-agent-name" /></F>
          <F label="Commission">
            <div className="flex gap-2">
              <Input type="number" step="0.01" min="0" value={form.agent_commission_value} onChange={(e) => setForm({ ...form, agent_commission_value: e.target.value })} className="flex-1" placeholder="e.g. 5 or 25000" data-testid="of-agent-comm-value" />
              <select value={form.agent_commission_type} onChange={(e) => setForm({ ...form, agent_commission_type: e.target.value })} className="h-10 border border-slate-200 rounded-md px-2 text-sm" data-testid="of-agent-comm-type">
                <option value="percent">% of order</option>
                <option value="amount">₹ amount</option>
              </select>
            </div>
          </F>
        </>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left pk-table-header ${className}`}>{children}</th>;
}
function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function OrderDetailDialog({ viewing, onClose, isMaster, canEdit, currentUserId, onDelete, onCancel, onEdit, readOnly = false }) {
  const [editSalesOpen, setEditSalesOpen] = useState(false);
  if (!viewing) return null;
  const { order, dispatches } = viewing;
  // Backward compat: `invoices` is preferred; fall back to single `invoice` if older payload.
  const invoices = Array.isArray(viewing.invoices)
    ? viewing.invoices
    : (viewing.invoice ? [viewing.invoice] : []);
  const orderType = order.order_type || "Product";
  const isService = orderType === "Service";
  const isCancelled = order.status === "Cancelled";
  // Order is locked once any active (non-cancelled) invoice exists.
  const hasActiveInvoice = invoices.some((i) => !i.is_cancelled);
  const isCreator = !!currentUserId && order.created_by_id === currentUserId;
  const canEditThisOrder = (canEdit || isCreator) && !isCancelled && !hasActiveInvoice;
  // Sales attribution can be edited ANY time (even when locked) — Master/Accounts only.
  const canEditSalesmen = canEdit && !isCancelled;
  return (
    <Dialog open={!!viewing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5">Order {order.order_no} <OrderTypeTag type={order.order_type} testid="order-detail-type" /></span>
            <Badge className={ORDER_STATUS_COLOR[order.status] || "bg-slate-100"}>{order.status}</Badge>
            <Badge className={PAY_COLOR[order.payment_status] || "bg-slate-100"}>{order.payment_status}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2" data-testid="order-detail">
          {isCancelled && (
            <div className="border border-red-200 bg-red-50 rounded-md p-3" data-testid="order-cancelled-banner">
              <div className="flex items-center gap-2">
                <Badge className="bg-red-600 text-white hover:bg-red-600 text-[10px]">CANCELLED</Badge>
                <span className="text-sm font-semibold text-red-700">{order.cancel_reason || "Order cancelled"}</span>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Cancelled by <span className="font-semibold">{order.cancelled_by_name || "—"}</span>
                {order.cancelled_at ? <> · {new Date(order.cancelled_at).toLocaleString()}</> : null}
              </div>
            </div>
          )}
          {order.allow_multiple_invoices && !isCancelled && (
            <div className="border border-amber-200 bg-amber-50/60 rounded-md p-2.5 text-[12px] text-amber-800" data-testid="order-multi-invoice-banner">
              <span className="font-semibold">Multiple invoices enabled</span> — Accounts may raise more than one invoice for this order (admin override).
            </div>
          )}
          {/* Stage timeline */}
          <Stages order={order} invoice={invoices[0] || null} dispatches={dispatches} />

          {/* Important Remark from the client master — top of every view */}
          <ClientImportantRemark remark={viewing.client?.important_remark || order.client_important_remark} />

          {/* Order section */}
          <Section title="Order Details" icon={<FileText size={14} />}>
            <Grid>
              <KV k="Client" v={order.client_name} />
              <KV k="Invoice Company" v={order.invoice_company_name} />
              <KV k="Order Type" v={orderType} />
              <KV k="Contact" v={`${order.contact_name || "—"} · ${order.contact_number || "—"}`} />
              <KV k="Billing" v={[order.billing_address, order.billing_district, order.billing_state, order.billing_pincode].filter(Boolean).join(", ")} full />
              <KV
                k="Shipping"
                v={(() => {
                  // If shipping is explicitly same-as-billing OR no shipping
                  // address was captured, surface the billing address so
                  // downstream teams (esp. Dispatch) can see exactly where
                  // to ship.
                  const sameAsBilling = order.shipping_same_as_billing || !order.shipping_address;
                  if (sameAsBilling) {
                    const addr = [order.billing_address, order.billing_district, order.billing_state, order.billing_pincode].filter(Boolean).join(", ");
                    return `${addr || "—"} (same as billing)`;
                  }
                  return [order.shipping_address, order.shipping_district, order.shipping_state, order.shipping_pincode].filter(Boolean).join(", ");
                })()}
                full
              />
              <KV k="Sales Associate" v={order.sale_associate_name} />
              <KV k="Commission" v={order.commission} />
              <KV k="Product/Service" v={order.product_service} />
              <KV k="Total Value" v={`₹ ${order.total_value}`} />
              {order.is_gem_contract && (
                <>
                  <KV k="GeM Contract No." v={order.gem_contract_no} />
                  <KV k="Contract Date" v={order.gem_contract_date} />
                  <KV k="Last Delivery Date" v={order.gem_last_delivery_date} />
                </>
              )}
              {order.has_po && (
                <>
                  <KV k="PO Number" v={order.po_number} />
                  <KV k="PO Date" v={order.po_date} />
                  {order.po_url && <KV k="PO Doc" v={<a href={order.po_url} target="_blank" rel="noreferrer" className="text-[var(--pk-accent)] underline">Open</a>} />}
                </>
              )}
              {order.remark && <KV k="Remark" v={order.remark} full />}
              <KV k="Created By" v={`${order.created_by_name} · ${new Date(order.created_at).toLocaleString()}`} full />
            </Grid>
          </Section>

          {/* Change log — every PATCH on the order is recorded here.
              Backend gates this read to Master/Accounts/Creator (403 otherwise). */}
          <OrderChangeLogPanel orderId={order.id} />

          {Array.isArray(order.edit_history) && order.edit_history.length > 0 && (
            <Section title={`Edit History (${order.edit_history.length})`} icon={<FileText size={14} />}>
              <div className="space-y-2">
                {order.edit_history.map((h, i) => (
                  <div key={`${h.changed_at || ""}-${i}`} className="border border-amber-100 bg-amber-50/50 rounded-md p-2.5 text-[12px]" data-testid={`order-edit-history-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800">{h.changed_by_name || "—"} <span className="text-[10px] text-slate-500 font-normal">· {h.changed_by_role || ""}</span></span>
                      <span className="mono text-[10px] text-slate-500">{h.changed_at ? new Date(h.changed_at).toLocaleString() : ""}</span>
                    </div>
                    <div className="text-slate-700 mt-1">{h.remark}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title={isService ? `Invoices (${invoices.length})` : "Invoice"} icon={<Receipt size={14} />}>
            {invoices.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Awaiting Accounts team to generate invoice.</div>
            ) : isService ? (
              <div className="space-y-3">
                {invoices.map((inv) => (
                  <div key={inv.id} className={`border ${inv.is_cancelled ? "border-red-200 bg-red-50/40 opacity-70" : "border-slate-200"} rounded-md p-3`} data-testid={`order-invoice-${inv.invoice_no}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-slate-900 mono">{inv.is_cancelled ? <span className="line-through">{inv.invoice_no}</span> : inv.invoice_no}</div>
                      <Badge className={PAY_COLOR[inv.is_cancelled ? "Cancelled" : inv.payment_status] || "bg-slate-100"}>{inv.is_cancelled ? "Cancelled" : inv.payment_status}</Badge>
                    </div>
                    <Grid>
                      <KV k="Invoice Date" v={inv.invoice_date} />
                      <KV k="Amount" v={`₹ ${inv.amount}`} />
                      {inv.due_date && <KV k="Due Date" v={inv.due_date} />}
                      {inv.gem_invoice_no && <KV k="GeM Invoice" v={inv.gem_invoice_no} />}
                      {inv.remark && <KV k="Remark" v={inv.remark} full />}
                      {inv.is_cancelled && <KV k="Cancellation Reason" v={`${inv.cancel_reason || "—"} (by ${inv.cancelled_by_name || "—"})`} full />}
                    </Grid>
                  </div>
                ))}
              </div>
            ) : (
              <Grid>
                <KV k="Invoice No" v={invoices[0].is_cancelled ? <span className="line-through">{invoices[0].invoice_no}</span> : invoices[0].invoice_no} />
                <KV k="Invoice Date" v={invoices[0].invoice_date} />
                <KV k="Amount" v={`₹ ${invoices[0].amount}`} />
                <KV k="Payment" v={<Badge className={PAY_COLOR[invoices[0].is_cancelled ? "Cancelled" : invoices[0].payment_status] || "bg-slate-100"}>{invoices[0].is_cancelled ? "Cancelled" : invoices[0].payment_status}</Badge>} />
                {invoices[0].due_date && <KV k="Due Date" v={invoices[0].due_date} />}
                {invoices[0].gem_invoice_no && <KV k="GeM Invoice" v={invoices[0].gem_invoice_no} />}
                {invoices[0].remark && <KV k="Remark" v={invoices[0].remark} full />}
                {invoices[0].is_cancelled && <KV k="Cancellation Reason" v={`${invoices[0].cancel_reason || "—"} (by ${invoices[0].cancelled_by_name || "—"})`} full />}
              </Grid>
            )}
          </Section>

          {!isService && (
          <Section title={`Dispatch & Tracking Details (${dispatches.length})`} icon={<Truck size={14} />}>
            {dispatches.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No dispatch entries yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="px-2 py-2 text-left pk-table-header">Tracking</th>
                      <th className="px-2 py-2 text-left pk-table-header">Partner</th>
                      <th className="px-2 py-2 text-left pk-table-header">Items / Qty</th>
                      <th className="px-2 py-2 text-left pk-table-header">Boxes</th>
                      <th className="px-2 py-2 text-left pk-table-header">Cost</th>
                      <th className="px-2 py-2 text-left pk-table-header">Status</th>
                      <th className="px-2 py-2 text-left pk-table-header">Dispatched On</th>
                      <th className="px-2 py-2 text-left pk-table-header">Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatches.map((d) => {
                      const recalled = !!d.recalled;
                      return (
                      <tr key={d.id} className={`border-b border-slate-100 ${recalled ? "opacity-60" : ""}`}>
                        <td className="px-2 py-2 mono">{recalled ? <span className="line-through">{d.tracking_no}</span> : d.tracking_no}</td>
                        <td className="px-2 py-2">{d.tracking_partner}</td>
                        <td className="px-2 py-2"><span className="mono">{d.item_sn}</span> · {d.quantity}</td>
                        <td className="px-2 py-2 mono">{d.no_of_boxes}</td>
                        <td className="px-2 py-2 mono">₹ {d.cost}</td>
                        <td className="px-2 py-2"><Badge className={recalled ? "bg-red-100 text-red-700 hover:bg-red-100" : d.status === "Delivered" ? "bg-green-100 text-green-700 hover:bg-green-100" : d.status === "In Transit" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>{recalled ? "Recalled" : d.status}</Badge></td>
                        <td className="px-2 py-2 mono whitespace-nowrap">{d.created_at ? new Date(d.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td className="px-2 py-2">{d.remark || "—"}{recalled && d.recall_tracking_no ? <div className="text-[10px] text-red-600">Return: {d.recall_tracking_no} {d.recall_partner ? `· ${d.recall_partner}` : ""}</div> : null}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
          )}
        </div>

        <DialogFooter>
          {!readOnly && (<>
          {canEditThisOrder && (
            <Button variant="outline" onClick={() => onEdit(order)} data-testid="order-edit">
              Edit Order
            </Button>
          )}
          {canEditSalesmen && (
            <Button
              variant="outline"
              onClick={() => setEditSalesOpen(true)}
              className="text-indigo-700 border-indigo-200 hover:bg-indigo-50 dark:text-indigo-300 dark:border-indigo-800 dark:hover:bg-indigo-900/20"
              data-testid="order-edit-salesmen"
            >
              <Users size={12} className="mr-1" /> Edit Sales Attribution
            </Button>
          )}
          {canEdit && !isCancelled && (
            <Button variant="outline" onClick={() => onCancel(order.id, order.order_no)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="order-cancel">
              <X size={12} className="mr-1" /> Mark Cancelled
            </Button>
          )}
          {isMaster && (
            <Button variant="outline" onClick={() => onDelete(order.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="order-delete">
              <Trash2 size={12} className="mr-1" /> Delete Order
            </Button>
          )}
          {hasActiveInvoice && !isCancelled && (
            <span className="text-[11px] text-amber-700 italic mr-auto self-center" data-testid="order-locked-badge">
              🔒 Order locked — an invoice has been generated. Edit the invoice instead. <span className="font-semibold">Sales attribution remains editable.</span>
            </span>
          )}
          </>)}
          <Button variant="outline" onClick={onClose} data-testid="order-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
      <EditSalesAttributionDialog
        open={editSalesOpen}
        onOpenChange={setEditSalesOpen}
        order={order}
        onSaved={() => { setEditSalesOpen(false); onClose(); }}
      />
    </Dialog>
  );
}

function Stages({ order, invoice, dispatches }) {
  const hasInvoice = !!invoice;
  const hasDispatch = (dispatches || []).length > 0;
  const isComplete = order.status === "Complete Delivered";
  const stages = [
    { label: "Order Created", done: true },
    { label: "Invoice Generated", done: hasInvoice },
    { label: "Dispatched", done: hasDispatch },
    { label: "Complete Delivered", done: isComplete },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="order-stages">
      {stages.map((s, i) => (
        <div key={s.label} className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border ${
            s.done ? "bg-[var(--pk-accent-subtle)] border-[var(--pk-accent)] text-[var(--pk-accent)]" :
            "bg-slate-50 border-slate-200 text-slate-400"
          }`}>
            <span className="w-4 h-4 rounded-full grid place-items-center mono text-[9px] bg-white border">
              {s.done ? "✓" : i + 1}
            </span>
            {s.label}
          </div>
          {i < stages.length - 1 && <div className={`h-px w-6 ${stages[i+1].done ? "bg-[var(--pk-accent)]" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="border border-slate-200 rounded-md">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 text-[12px] font-semibold text-slate-800">
        {icon} {title}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">{children}</div>;
}
function KV({ k, v, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-sm text-slate-900 mt-0.5">{v || "—"}</div>
    </div>
  );
}


function OrderChangeLogPanel({ orderId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/orders/${orderId}/change-log`)
      .then(({ data }) => { if (alive) { setData(data); setForbidden(false); } })
      .catch((e) => { if (alive) setForbidden(e.response?.status === 403); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orderId]);
  if (loading || forbidden) return null;
  const logs = data?.logs || [];
  if (logs.length === 0) return null;
  return (
    <Section title={`Order Change Log (${logs.length})`} icon={<FileText size={14} />}>
      <div className="space-y-2" data-testid="order-change-log">
        {logs.map((l, i) => (
          <div key={`${l.at || ""}-${i}`} className="border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 rounded-md p-2.5 text-[12px]" data-testid={`order-change-log-entry-${i}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {l.by_name || "—"}
                {l.by_role ? <span className="text-[10px] text-slate-500 font-normal"> · {l.by_role}</span> : null}
              </span>
              <span className="mono text-[10px] text-slate-500">{l.at ? new Date(l.at).toLocaleString() : ""}</span>
            </div>
            {l.source && <div className="text-[10.5px] text-violet-700 dark:text-violet-300 mb-1">via {l.source}</div>}
            {(l.changes || []).map((c, j) => (
              <div key={j} className="text-[11px] text-slate-600 dark:text-slate-300 pl-2 border-l-2 border-slate-300 dark:border-slate-600 mb-0.5">
                <span className="mono font-semibold text-slate-700 dark:text-slate-200">{c.field}</span>:
                {" "}<span className="text-rose-600 dark:text-rose-400 line-through">{String(c.from ?? "—")}</span>
                {" → "}<span className="text-emerald-700 dark:text-emerald-400">{String(c.to ?? "—")}</span>
              </div>
            ))}
            {l.remark && <div className="text-[11px] text-slate-700 dark:text-slate-300 mt-1 italic">{l.remark}</div>}
          </div>
        ))}
      </div>
    </Section>
  );
}


/**
 * EditSalesAttributionDialog — edit Sales Team on a single order. Allowed
 * even when the order is locked (active invoice exists). Uses the dedicated
 * `PATCH /api/orders/{id}/salesmen` lock-bypass endpoint.
 */
export function EditSalesAttributionDialog({ open, onOpenChange, order, onSaved }) {
  const [people, setPeople] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setSalesmen((order.salesmen || []).map((s) => ({ user_id: s.user_id, percent: parseFloat(s.percent) || 0 })));
    setRemark("");
    api.get("/employees", { params: { active: true, limit: 500 } }).then(({ data }) => {
      setPeople(data.items || data || []);
    }).catch(() => setPeople([]));
  }, [open, order]);

  if (!order) return null;

  const totalNum = parseFloat(String(order.total_value || "").replace(/[^0-9.]/g, "")) || 0;
  const sumPct = salesmen.reduce((s, x) => s + (parseFloat(x.percent) || 0), 0);
  const pctOk = salesmen.length === 0 || Math.abs(sumPct - 100) < 0.5;
  const nameOf = (uid) => people.find((p) => p.id === uid)?.name || `User ${uid?.slice?.(-4) || ""}`;

  const add = (uid) => {
    if (!uid || salesmen.some((s) => s.user_id === uid)) return;
    const arr = [...salesmen, { user_id: uid, percent: 0 }];
    const each = +(100 / arr.length).toFixed(2);
    arr.forEach((s) => { s.percent = each; });
    setSalesmen(arr);
  };
  const remove = (uid) => {
    const arr = salesmen.filter((s) => s.user_id !== uid);
    if (arr.length > 0) {
      const each = +(100 / arr.length).toFixed(2);
      arr.forEach((s) => { s.percent = each; });
    }
    setSalesmen(arr);
  };
  const setPct = (uid, val) => setSalesmen(salesmen.map((s) => s.user_id === uid ? { ...s, percent: val } : s));
  const equalSplit = () => {
    if (!salesmen.length) return;
    const each = +(100 / salesmen.length).toFixed(2);
    setSalesmen(salesmen.map((s) => ({ ...s, percent: each })));
  };

  const submit = async () => {
    if (!remark.trim()) { toast.error("A remark is required."); return; }
    if (salesmen.length > 0 && !pctOk) { toast.error("Salesmen % must add up to 100."); return; }
    setSaving(true);
    try {
      await api.patch(`/orders/${order.id}/salesmen`, {
        salesmen: salesmen.map((s) => ({ user_id: s.user_id, percent: parseFloat(s.percent) || 0 })),
        edit_remark: remark.trim(),
      });
      toast.success("Sales attribution updated");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="edit-salesmen-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <Users size={16} className="text-indigo-600" /> Edit Sales Attribution — {order.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-slate-600 dark:text-slate-300">
          Sales credit can be changed any time — even on a locked order. Total Order Value:
          <span className="font-bold mono ml-1">₹ {totalNum.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-2">
          <select
            onChange={(e) => { add(e.target.value); e.target.value = ""; }}
            className="h-9 text-sm rounded-md px-3 bg-white dark:bg-[hsl(var(--card))] border border-indigo-200 dark:border-indigo-800 text-slate-800 dark:text-slate-100 font-medium"
            data-testid="esa-add"
          >
            <option value="">+ Add associate…</option>
            {people.filter((p) => !salesmen.some((s) => s.user_id === p.id)).map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.employee_code ? ` · ${p.employee_code}` : ""}</option>
            ))}
          </select>
          {salesmen.length > 0 && (
            <>
              <span
                className={`text-[11px] mono font-bold px-2.5 py-1 rounded-full ${
                  pctOk ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 animate-pulse"
                }`}
                data-keep-color
              >
                Total {sumPct.toFixed(2)}% {pctOk ? "✓" : "(must be 100%)"}
              </span>
              <button type="button" onClick={equalSplit} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 transition-all" data-keep-color>
                Equal Split
              </button>
            </>
          )}
        </div>

        {salesmen.length === 0 && (
          <div className="text-[11.5px] italic text-slate-500 dark:text-slate-400 px-3 py-3 mt-2 bg-white dark:bg-[hsl(var(--muted))] rounded-md border border-dashed border-indigo-200 dark:border-indigo-900">
            No associates tagged. Add one or more.
          </div>
        )}

        <div className="space-y-1.5 mt-2">
          {salesmen.map((s, idx) => {
            const amt = totalNum * (parseFloat(s.percent) || 0) / 100;
            return (
              <div key={s.user_id} className="flex items-center gap-2 bg-white dark:bg-[hsl(var(--card))] border border-indigo-100 dark:border-indigo-900/60 rounded-md px-2.5 py-1.5" data-testid={`esa-row-${s.user_id}`}>
                <span className="w-6 h-6 grid place-items-center rounded-full bg-indigo-600 text-white text-[11px] font-extrabold shrink-0" data-keep-color>{idx + 1}</span>
                <span className="text-sm font-semibold flex-1 truncate text-slate-800 dark:text-slate-100">{nameOf(s.user_id)}</span>
                <Input type="number" min="0" max="100" step="0.01" value={s.percent} onChange={(e) => setPct(s.user_id, parseFloat(e.target.value) || 0)} className="h-8 w-20 text-xs text-center" />
                <span className="text-[11px] mono text-slate-500 dark:text-slate-400">%</span>
                <span className="text-[11.5px] mono font-bold text-emerald-700 dark:text-emerald-300 w-28 text-right shrink-0">₹ {amt.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
                <Button size="sm" variant="outline" onClick={() => remove(s.user_id)} className="h-8 w-8 p-0 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-900 shrink-0"><X size={12}/></Button>
              </div>
            );
          })}
        </div>

        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Remark *</Label>
          <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Why is this changing?" className="mt-1" data-testid="esa-remark" />
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !remark.trim() || (salesmen.length > 0 && !pctOk)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-keep-color data-testid="esa-save">
            {saving ? "Saving…" : "Save Sales Attribution"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
